import config from "appConfig";

const theory = `
<h3 class=sectionHead>Impact sound insulation</h3>
<p>Vibration transmission (impact sound) in buildings is another important issue for human comfort and health in a living and work environment. In the previous section, the focus was on the prediction and auralization of airborne sound insulation. In this section, we now move forward towards the discussion of impact sound. The source, therefore, is to be characterized by its force exciting the building structure, usually a “floor” ISO 10140-2 [1]. As soon as the velocity in the structure is known, transmission and radiation of sound in the receiving room can be considered. The basic definitions and methods for predicting and evaluating impact sounds are given in next section where we will discuss available prediction model defined by ISO 12354-2 [2], from which suitable techniques for auralization will be derived.
<h4 class=subsectionHead>Impact sound insulation auralization</h4>
<p>
   Impact sound in buildings is characterized by exciting a floor with the tapping machine (TM) (ISO 16283-2 [3]), by measuring the normalised impact sound level in the receiving room,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   . and the equivalent absorption area,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   , of the receiving room obtained from the reverberation time.
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi>l</mi>
            <mi>o</mi>
            <mi>g</mi>
            <mfrac>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mn>0</mn>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   Assuming a linear behaviour the sound pressure resulting from a different force source (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>F</mi>
      <mi>S</mi>
   </math>
   ) can be obtained by the expression given in Equation 2.
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>F</mi>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>T</mi>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mi>F</mi>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mi>F</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>F</mi>
            <mi>S</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>F</mi>
            <mi>S</mi>
         </mrow>
      </msub>
   </math>
   denote the sound pressure and force, respectively, caused by an arbitrary force source. The sound pressure excited by the tapping machine is known by measurement from eq.1. We can now find an expression for the sound pressure in the receiving room depending on the normalised impact sound level, with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mi>j</mi>
         </mrow>
      </msub>
   </math>
   as the normalised impact sound levels of the independent paths and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>o</mi>
         </mrow>
      </msub>
   </math>
   is the reference sound pressure of 20
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>μ</mi>
      <mi>P</mi>
      <mi>a</mi>
   </math>
   .
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>F</mi>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mi>F</mi>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi>F</mi>
                     </mrow>
                     <mrow>
                        <mi>T</mi>
                        <mi>M</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
            </mfrac>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>j</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>5</mn>
               </mrow>
            </munderover>
            <msup>
              <mrow>
                <mn>1</mn>
                <mn>0</mn>
              </mrow>
                <mrow>
                  <mfrac>
                  <mrow>
                    <msub>
                        <mrow>
                          <mi>L</mi>
                        </mrow>
                        <mrow>
                          <mi>n</mi>
                          <mo>,</mo>
                          <mi>j</mi>
                        </mrow>
                    </msub>
                  </mrow>
                  <mrow>
                    <mn>1</mn>
                    <mn>0</mn>
                  </mrow>
              </mfrac>
              </mrow>
            </msup>
            <msubsup>
              <mrow>
                <mi>p</mi>
              </mrow>
              <mrow>
                <mn>2</mn>
              </mrow>
              <mrow>
                <mi>o</mi>
              </mrow>
                  </msubsup>
            <mfrac>
               <mrow>
                  <mi>T</mi>
               </mrow>
               <mrow>
                  <mn>0.5</mn>
                  <mi>s</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mn>3</mn>
                  <mn>0</mn>
               </mrow>
            </mfrac>

         </math>
         <td class=eq-no>(3)
         </table>
         <p>
            Instead of using measurement results, the impact sound pressure levels can also be obtained by simulation software [2]. It remains the task to create input forces for the tapping machine and for other sources. As an example, one-third octave spectra of measured forces for a tapping machine, a modified tapping machine (with resilient interlayer), and a “heavy impact” rubber ball according to ISO 16283-2 are shown.
            <figure class=figure>
         <p><img src="${config.ASSETS_URL}/03/14/theory/figure-1.svg" alt="Figure 1. Ground reaction forces for a tapping machine, a modified tapping machine and a rubber ball in one-third octave bands. [4]"><figcaption class=caption>Figure 1. Ground reaction forces for a tapping machine, a modified tapping machine and a rubber ball in one-third octave bands. [4]</figcaption></figure>
         <p>
            In order to auralize the impact sound a force-time signal of the suitable length is constructed from several measured force pulses of the source. For example, from two or three force pulses of one hammer of a tapping machine a force-time signal can be constructed by appending the pulses at an interval of 0.1 seconds. Jittering might be introduced in time and amplitude of the force-time signal to produce the sound more natural.
            <figure class=figure>
         <p><img src="${config.ASSETS_URL}/03/14/theory/figure-2.svg" alt="Figure 2. Force impulse sequence for a tapping machine (Amplitude verses time). [4]"><figcaption class=caption>Figure 2. Force impulse sequence for a tapping machine (Amplitude verses time). [4]</figcaption></figure>
         <h3 class=likesectionHead>References</h3>
         <dl>
             <dt>[1]</dt><dd>I. 10140-2. Acoustics—laboratory measurement of sound insulation of building elements. Measurement of impact sound insulation, 2010.</dd>
             <dt>[2]</dt><dd>I. 12354-2. Building acoustics - estimation of acoustic performance of buildings from the performance of elements - part 2: Impact sound insulation between rooms, 2017.</dd>
             <dt>[3]</dt><dd>I. 16283-2. Acoustics–field measurement of sound insulation in buildings and of building elements–part 2: Impact sound insulation, 2015.</dd>
             <dt>[4]</dt><dd>R. Thaden. Auralisation in building acoustics. PhD thesis, RWTH University Aachen: Germany, 2007.</dd>
         </dl>`;

export default theory;
